<template>
  <div class="tw-flex-wrap tw-mb-1">
    <draggable v-model="internalModelValue" item-key="label">
      <template #item="{ element }">
        <WdsLabel :key="element.label" sm class="tw-mr-1 tw-cursor-move">
          <div class="tw-flex tw-items-center tw-gap-xs">
            {{ element.label }}
            <button class="label-button" @click="() => handleRemove(element)">
              <WdsIcon name="close" :size="12" />
            </button>
          </div>
        </WdsLabel>
      </template>
    </draggable>
  </div>
  <WdsSelect
    v-model:search="searchInput"
    searchable
    :list="list"
    :info="canAddUnsupportedTypes ? addSkillText : ''"
    :error="suggestionError"
    :name="name"
    data-test="type-suggestion-skills"
    @load-more="loadMore"
    @update:model-value="handleUpdate"
    @toggle-select="handleToggle"
  >
    <template #prefix>
      <WdsSpinner v-if="loading" indeterminate :size="16" data-test="type-suggestion-skills-spinner" />
      <WdsIcon v-else name="search" class="inline" :size="16" />
    </template>
    <template #header>
      <div class="tw-text-gray-600">{{ placeholder }}</div>
    </template>
  </WdsSelect>
  <TypeSuggestionsSkills
    v-if="showTypeSuggestions"
    :skills-for-suggestions="skillsForSuggestions"
    :excluded-skill-names="excludedSkillNames"
    @add-suggestion="handleAddSuggestion"
  />
</template>

<script setup lang="ts">
import { WdsSelect, WdsLabel, WdsIcon, WdsSpinner } from '@worksome/wds-components'
import draggable from 'vuedraggable'
import { useDebounceFn, useVModel } from '@vueuse/core'
import { ref, watch, Ref, computed } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { SkillsQuery } from '@/components/TypeSuggestion/graphql/queries'
import { $t } from '@/composables/translation'
import { SkillableType } from '@/gql/graphql'
import { merge } from '@/utils/tools'
import TypeSuggestionsSkills from '@/components/TypeSuggestion/TypeSuggestionsSkills.vue'

const emit = defineEmits(['update:model-value'])

const searchInput = ref('')
const skillsForSuggestions: Ref<string[]> = ref([])

type Option = {
  label: string
  value?: string
}

const props = withDefaults(
  defineProps<{
    modelValue: Option[]
    error?: string | boolean
    placeholder?: string
    name?: string
    canAddUnsupportedTypes?: boolean
    addSkillText?: string
    skillableType: SkillableType[]
    showTypeSuggestions?: boolean
  }>(),
  {
    error: undefined,
    placeholder: $t('Add a skill'),
    name: undefined,
    // eslint-disable-next-line vue/no-boolean-default
    canAddUnsupportedTypes: true,
    addSkillText: $t('Add a new skill by pressing enter'),
    showTypeSuggestions: false,
  },
)

const internalModelValue = useVModel(props, 'modelValue', emit)

const { result, loading, load, fetchMore, error: queryError } = useLazyQuery(SkillsQuery)

const list: Ref<Option[]> = ref([])

watch(
  () => result.value,
  () => {
    if (!searchInput.value) {
      list.value = []
    } else {
      list.value =
        result?.value?.skills.data
          .map(({ name }) => ({ label: name, value: name }))
          .filter(({ value }) => !props.modelValue.find((item) => item.value === value)) || []
    }
  },
)

const debouncedSearch = useDebounceFn(
  () =>
    load(
      SkillsQuery,
      {
        search: searchInput.value,
        skillableType: props.skillableType,
      },
      {
        clientId: 'publicApi',
      },
    ),
  200,
)

watch(searchInput, () => {
  if (searchInput.value) {
    debouncedSearch()
  }
})

const loadMore = () => {
  const { hasMorePages, currentPage } = result?.value?.skills.paginatorInfo ?? {}
  if (!hasMorePages || !currentPage) {
    return
  }
  fetchMore({
    variables: {
      page: currentPage + 1,
    },
    updateQuery(prev, { fetchMoreResult }) {
      return merge(prev, fetchMoreResult)
    },
  })
}

const handleUpdate = (option: Option) => {
  skillsForSuggestions.value = [option.label]
  emit('update:model-value', [...props.modelValue, option])
  list.value = []
}

const handleRemove = (option: Option) => {
  emit(
    'update:model-value',
    props.modelValue.filter(({ value }) => value !== option.value),
  )
}

// Add a new item to the list that does not already exist
const handleToggle = (event: Event) => {
  if (
    props.canAddUnsupportedTypes &&
    event instanceof KeyboardEvent &&
    event.key === 'Enter' &&
    searchInput.value &&
    !props.modelValue.find((option) => option.label === searchInput.value)
  ) {
    emit('update:model-value', [
      ...props.modelValue,
      { label: searchInput.value, value: searchInput.value },
    ])
  }
}

const excludedSkillNames = computed(() => props.modelValue.map(({ label }) => label))

const suggestionError = computed(() => props.error || queryError.value?.message)

const handleAddSuggestion = (suggestion: string) => {
  if (!props.modelValue.find((option) => option.value === suggestion)) {
    emit('update:model-value', [...props.modelValue, { label: suggestion, value: suggestion }])
  }
}
</script>

<style scoped lang="scss">
.label-button {
  @apply tw-bg-transparent;
  @apply tw-border-none;
  @apply tw-p-0;
  @apply tw-cursor-pointer;
}

:deep(.wds-select-wrapper .wds-select-button-display) {
  @apply tw-pl-2;
}

:deep(.wds-select-wrapper button.icon-down) {
  @apply tw-hidden;
}
</style>
